// Change #100463 - the Enterprise plan fields CRMit edits (Super Admin only):
// which master product it is based on (what a seat grants), plan name, users
// included, the secret allow-up-to tolerance, and the bespoke agreed price and
// how often CRMit invoices it. Used by the Company Report switch, Back End >
// Company Details > Subscriptions and Super Admin > Enterprise Plans.

import React from "react";
import { Input } from "reactstrap";

export const PRICE_PERIODS = [
  { value: "MONTHLY", label: "per month" },
  { value: "ANNUALLY", label: "per year" },
];

/** "" when fine, else the problem. */
export const validateEnterprisePlan = (v) => {
  const max = Number(v?.MaxUsers);
  if (!(max > 0)) return "Enter how many users the plan includes";
  if (v?.MaxUsersTolerance !== "" && v?.MaxUsersTolerance != null && Number(v.MaxUsersTolerance) < max) {
    return "The tolerance cannot be lower than the users included";
  }
  if (v?.PriceLocalSubTotal !== "" && v?.PriceLocalSubTotal != null && !(Number(v.PriceLocalSubTotal) >= 0)) {
    return "Enter a valid price";
  }
  if (v?.RrpLocalSubTotal !== "" && v?.RrpLocalSubTotal != null && Number(v.RrpLocalSubTotal) < Number(v.PriceLocalSubTotal || 0)) {
    return "The price cannot be more than the RRP";
  }
  if (v?.needsFeeDates && v?.PriceLocalSubTotal !== "" && v?.PriceLocalSubTotal != null && (!v.FeeStartDate || !v.FeeRenewalDate)) {
    return "Enter when billing starts and the first charge date";
  }
  return "";
};

/** API body fields from the form values (blank -> null). */
export const enterprisePlanPayload = (v) => ({
  PlanName: v.PlanName || "",
  MaxUsers: Number(v.MaxUsers),
  MaxUsersTolerance: v.MaxUsersTolerance === "" || v.MaxUsersTolerance == null ? null : Number(v.MaxUsersTolerance),
  ...(v.BaseProduct_Id ? { BaseProduct_Id: Number(v.BaseProduct_Id) } : {}),
  PriceLocalSubTotal: v.PriceLocalSubTotal === "" || v.PriceLocalSubTotal == null ? null : Number(v.PriceLocalSubTotal),
  PricePeriod: v.PricePeriod || "MONTHLY",
  // Change #100463 - RRP (price = RRP - discount) and, for a plan not billed
  // yet, when billing starts and the first charge date.
  RrpLocalSubTotal: v.RrpLocalSubTotal === "" || v.RrpLocalSubTotal == null ? null : Number(v.RrpLocalSubTotal),
  ...(v.FeeStartDate ? { FeeStartDate: v.FeeStartDate } : {}),
  ...(v.FeeRenewalDate ? { FeeRenewalDate: v.FeeRenewalDate } : {}),
});

const discountOf = (v) => {
  const rrp = v?.RrpLocalSubTotal;
  const price = v?.PriceLocalSubTotal;
  if (rrp === "" || rrp == null || price === "" || price == null) return null;
  return Math.max(0, Number(rrp) - Number(price)).toFixed(2);
};

export default function EnterprisePlanFields({ value, onChange, products = [], businessName = "", priceReady = true }) {
  const set = (patch) => onChange({ ...value, ...patch });
  const perUser =
    Number(value?.PriceLocalSubTotal) > 0 && Number(value?.MaxUsers) > 0
      ? Number(value.PriceLocalSubTotal) / Number(value.MaxUsers)
      : null;

  return (
    <div className="row g-3">
      {products.length > 0 && (
        <div className="col-12">
          <label className="form-label mb-1">Based on</label>
          <Input
            type="select"
            value={value?.BaseProduct_Id ?? ""}
            onChange={(e) => set({ BaseProduct_Id: e.target.value })}
          >
            <option value="">Keep current access</option>
            {products.map((p) => (
              <option key={p.Id} value={p.Id}>
                {p.Name} (#{p.Id})
              </option>
            ))}
          </Input>
          <div className="text-muted fs-12 mt-1">
            A seat gives the same access as this product. The price below replaces its price.
          </div>
        </div>
      )}
      <div className="col-12">
        <label className="form-label mb-1">Plan name</label>
        <Input
          placeholder={`Enterprise - ${businessName || "business name"}`}
          value={value?.PlanName ?? ""}
          onChange={(e) => set({ PlanName: e.target.value })}
        />
      </div>
      <div className="col-6">
        <label className="form-label mb-1">
          Users included <span className="text-danger">*</span>
        </label>
        <Input
          type="number"
          min={1}
          placeholder="e.g. 20"
          value={value?.MaxUsers ?? ""}
          onChange={(e) => set({ MaxUsers: e.target.value })}
        />
      </div>
      <div className="col-6">
        <label className="form-label mb-1">Allow up to</label>
        <Input
          type="number"
          min={1}
          placeholder="e.g. 25"
          value={value?.MaxUsersTolerance ?? ""}
          onChange={(e) => set({ MaxUsersTolerance: e.target.value })}
        />
        <div className="text-muted fs-12 mt-1">Never shown to the customer.</div>
      </div>
      <div className="col-4">
        <label className="form-label mb-1">RRP (ex VAT)</label>
        <div className="input-group">
          <span className="input-group-text">£</span>
          <Input
            type="number"
            min={0}
            step="0.01"
            placeholder="e.g. 795.00"
            disabled={!priceReady}
            value={value?.RrpLocalSubTotal ?? ""}
            onChange={(e) => {
              const rrp = e.target.value;
              const disc = discountOf(value);
              set({
                RrpLocalSubTotal: rrp,
                ...(rrp !== "" && disc != null ? { PriceLocalSubTotal: Math.max(0, Number(rrp) - disc).toFixed(2) } : {}),
              });
            }}
          />
        </div>
      </div>
      <div className="col-4">
        <label className="form-label mb-1">Discount</label>
        <div className="input-group">
          <span className="input-group-text">£</span>
          <Input
            type="number"
            min={0}
            step="0.01"
            placeholder="e.g. 110.00"
            disabled={!priceReady || value?.RrpLocalSubTotal === "" || value?.RrpLocalSubTotal == null}
            value={discountOf(value) ?? ""}
            onChange={(e) => {
              const d = e.target.value === "" ? 0 : Number(e.target.value);
              set({ PriceLocalSubTotal: Math.max(0, Number(value.RrpLocalSubTotal) - d).toFixed(2) });
            }}
          />
        </div>
      </div>
      <div className="col-4">
        <label className="form-label mb-1">Price (ex VAT)</label>
        <div className="input-group">
          <span className="input-group-text">£</span>
          <Input
            type="number"
            min={0}
            step="0.01"
            placeholder="e.g. 685.00"
            disabled={!priceReady}
            value={value?.PriceLocalSubTotal ?? ""}
            onChange={(e) => set({ PriceLocalSubTotal: e.target.value })}
          />
        </div>
      </div>
      <div className="col-6">
        <label className="form-label mb-1">Billed</label>
        <Input
          type="select"
          disabled={!priceReady}
          value={value?.PricePeriod || "MONTHLY"}
          onChange={(e) => set({ PricePeriod: e.target.value })}
        >
          {PRICE_PERIODS.map((p) => (
            <option key={p.value} value={p.value}>
              {p.label}
            </option>
          ))}
        </Input>
      </div>
      {value?.needsFeeDates && (
        <>
          <div className="col-6">
            <label className="form-label mb-1">Billing starts</label>
            <Input type="date" value={value?.FeeStartDate || ""} onChange={(e) => set({ FeeStartDate: e.target.value })} />
          </div>
          <div className="col-6">
            <label className="form-label mb-1">First charge</label>
            <Input type="date" value={value?.FeeRenewalDate || ""} onChange={(e) => set({ FeeRenewalDate: e.target.value })} />
          </div>
        </>
      )}
      <div className="col-12 text-muted fs-12">
        {!priceReady
          ? "The price can be set once the database update for change #100463 has run."
          : `${perUser != null ? `About £${perUser.toFixed(2)} per user. ` : ""}Billed like any subscription: the card is charged on each renewal (monthly plans; annual ones are not collected automatically). The users' seats are not charged.`}
      </div>
    </div>
  );
}
